// src/components/CapturaImagenesVisita.js
import React, { useState, useRef, useCallback, useEffect } from 'react';
import './CapturaImagenesVisita.css';
import { FaCamera, FaTimes, FaCheck, FaRedo, FaIdCard, FaCar, FaImage } from 'react-icons/fa';

const TIPOS_IMAGEN = {
    INE: {
        id: 'ine',
        titulo: 'Captura de INE/Identificación',
        descripcion: 'Por favor, captura una foto clara de la identificación oficial del visitante.',
        icono: <FaIdCard />,
    },
    CAJUELA: {
        id: 'cajuela',
        titulo: 'Captura de Cajuela',
        descripcion: 'Por favor, captura una foto del interior de la cajuela del vehículo.',
        icono: <FaCar />,
    },
    PLACA: {
        id: 'placa',
        titulo: 'Captura de Placa',
        descripcion: 'Por favor, captura una foto clara de la placa del vehículo.',
        icono: <FaImage />,
    },
};

const CapturaImagenesVisita = ({
    onComplete,
    onCancel,
    onExit,
    visitanteNombre = 'Visitante',
    tipoTransporte = 'En vehiculo',
}) => {
    console.log('🎬 [CapturaImagenesVisita] Componente montado!', { visitanteNombre, tipoTransporte });

    // Definir etapas dinámicamente según el tipo de transporte
    const getEtapas = useCallback(() => {
        if (tipoTransporte === 'A pie') return ['ine'];
        if (tipoTransporte === 'En moto') return ['ine', 'placa'];
        return ['ine', 'placa', 'cajuela']; // Default 'En vehiculo'
    }, [tipoTransporte]);

    const etapas = getEtapas();
    const [etapaActual, setEtapaActual] = useState(etapas[0]);
    const [imagenes, setImagenes] = useState({
        ine: null,
        cajuela: null,
        placa: null,
    });
    const [imagenCapturada, setImagenCapturada] = useState(null);
    const [mostrandoCamara, setMostrandoCamara] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [errorCamara, setErrorCamara] = useState('');
    const [mostrarInfo, setMostrarInfo] = useState(false);
    const [confirmarCancelacion, setConfirmarCancelacion] = useState(false);

    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const canvasRef = useRef(null);
    const warningRef = useRef(null);

    const tipoActual = TIPOS_IMAGEN[etapaActual.toUpperCase()];

    // Detener cámara cuando se desmonta el componente
    useEffect(() => {
        return () => {
            detenerCamara();
        };
    }, []);

    // Scroll automático al mensaje de advertencia cuando aparece
    useEffect(() => {
        if (confirmarCancelacion && warningRef.current) {
            warningRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [confirmarCancelacion]);

    const detenerCamara = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setMostrandoCamara(false);
    }, []);

    const iniciarCamara = useCallback(async () => {
        console.log('📷 [Cámara] Intentando iniciar cámara...');
        setErrorCamara('');
        setMostrandoCamara(true);
        setConfirmarCancelacion(false);

        try {
            console.log('📷 [Cámara] Solicitando permisos de cámara...');
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment' }
            });

            console.log('📷 [Cámara] Stream obtenido:', stream);

            // Asegurarse de que el video esté en el DOM
            await new Promise(resolve => setTimeout(resolve, 200));

            if (videoRef.current) {
                streamRef.current = stream;
                videoRef.current.srcObject = stream;

                await new Promise((resolve) => {
                    if (videoRef.current.readyState >= 2) {
                        resolve();
                    } else {
                        videoRef.current.onloadedmetadata = () => {
                            resolve();
                        };
                    }
                });
            } else {
                throw new Error('No se pudo acceder al elemento de video');
            }
        } catch (error) {
            console.error('📷 [Cámara] Error:', error);
            setMostrandoCamara(false);
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }
            setErrorCamara(`No se pudo acceder a la cámara: ${error.message || 'Error desconocido'}`);
        }
    }, []);

    const capturarFoto = useCallback(() => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
            if (blob) {
                const url = URL.createObjectURL(blob);
                setImagenCapturada({ blob, url });
                detenerCamara();
            } else {
                setErrorCamara('No se pudo capturar la imagen. Intenta nuevamente.');
            }
        }, 'image/jpeg', 0.9);
    }, [detenerCamara]);

    const retomar = useCallback(() => {
        if (imagenCapturada?.url) {
            URL.revokeObjectURL(imagenCapturada.url);
        }
        setImagenCapturada(null);
        iniciarCamara();
    }, [imagenCapturada, iniciarCamara]);

    const guardarImagenActual = useCallback(async () => {
        if (!imagenCapturada) return;

        const nuevasImagenes = {
            ...imagenes,
            [etapaActual]: imagenCapturada,
        };
        setImagenes(nuevasImagenes);

        // Avanzar a la siguiente etapa según la lista dinámica
        const indiceActual = etapas.indexOf(etapaActual);
        if (indiceActual < etapas.length - 1) {
            setEtapaActual(etapas[indiceActual + 1]);
            setImagenCapturada(null);
        } else {
            // Completado, devolver todas las imágenes recolectadas
            setGuardando(true);
            try {
                await onComplete(nuevasImagenes);
            } catch (error) {
                console.error("Error al completar captura:", error);
                setGuardando(false);
            }
        }
    }, [imagenCapturada, etapaActual, imagenes, etapas, onComplete]);

    const handleCancelar = useCallback(() => {
        if (!confirmarCancelacion) {
            setConfirmarCancelacion(true);
            return;
        }

        console.log('❌ [Cancelar] Usuario confirmó cancelar captura de imágenes');
        detenerCamara();
        // Limpiar URLs de imágenes
        Object.values(imagenes).forEach((img) => {
            if (img?.url) {
                URL.revokeObjectURL(img.url);
            }
        });
        if (imagenCapturada?.url) {
            URL.revokeObjectURL(imagenCapturada.url);
        }
        onCancel();
    }, [confirmarCancelacion, detenerCamara, imagenes, imagenCapturada, onCancel]);

    const obtenerProgreso = () => {
        const indiceActual = etapas.indexOf(etapaActual);
        return ((indiceActual + 1) / etapas.length) * 100;
    };

    return (
        <div className="captura-imagenes-overlay">
            <div className="captura-imagenes-modal">
                {/* Overlay de carga Zen */}
                {guardando && (
                    <div style={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                        backgroundColor: 'rgba(255,255,255,0.98)', zIndex: 999, display: 'flex',
                        flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        borderRadius: '24px', backdropFilter: 'blur(4px)',
                        transition: 'all 0.3s ease'
                    }}>
                        <div className="zen-loader-container" style={{ marginBottom: '25px', position: 'relative' }}>
                            <div style={{
                                width: '64px', height: '64px', border: '3px solid #f1f5f9',
                                borderTop: '3px solid #6366f1', borderRadius: '50%',
                                animation: 'spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite',
                            }} />
                            <div style={{
                                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                                color: '#6366f1', fontSize: '1.2rem'
                            }}>
                                {etapas.indexOf('ine') !== -1 && <FaIdCard />}
                            </div>
                        </div>
                        <h3 style={{ 
                            color: '#1e293b', 
                            marginBottom: '12px', 
                            fontWeight: '700', 
                            fontSize: '1.25rem',
                            letterSpacing: '-0.02em'
                        }}>
                             {etapas.includes('placa') ? 'Registrando Acceso' : 'Validando Invitación'}
                        </h3>
                        <p style={{ 
                            color: '#64748b', 
                            textAlign: 'center', 
                            padding: '0 30px', 
                            fontSize: '1rem',
                            lineHeight: '1.5',
                            maxWidth: '320px'
                        }}>
                            Por favor espera un momento, estamos procesando las imágenes y confirmando el acceso de <strong style={{color: '#475569'}}>{visitanteNombre}</strong>...
                        </p>
                        <style>
                            {`
                                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                                @keyframes pulse-zen { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }
                            `}
                        </style>
                    </div>
                )}

                {/* Header */}
                <div className="captura-header">
                    <div className="captura-header-content">
                        <div className="captura-icono">{tipoActual.icono}</div>
                        <div>
                            <h3>{tipoActual.titulo}</h3>
                            <p className="captura-visitante">Visitante: {visitanteNombre}</p>
                        </div>
                    </div>
                    <button
                        className="captura-btn-cerrar"
                        onClick={onExit || onCancel}
                        disabled={guardando}
                        aria-label="Cerrar"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Barra de progreso */}
                <div className="captura-progreso-container">
                    <div className="captura-progreso-barra">
                        <div
                            className="captura-progreso-fill"
                            style={{ width: `${obtenerProgreso()}%` }}
                        />
                    </div>
                    <span className="captura-progreso-texto">
                        Paso {etapas.indexOf(etapaActual) + 1} de {etapas.length}
                    </span>
                </div>

                {/* Mensaje de Confirmación de Cancelación */}
                {confirmarCancelacion && (
                    <div 
                        ref={warningRef}
                        className="captura-warning-cancellation" 
                        style={{
                            backgroundColor: '#fff7ed',
                            border: '2px solid #f97316', // Borde más visible (naranja fuerte)
                            borderRadius: '12px',
                            padding: '1.25rem',
                            marginBottom: '1.5rem',
                            textAlign: 'center',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            animation: 'pulse-warning 2s infinite'
                        }}
                    >
                        <p style={{ color: '#9a3412', fontWeight: '800', marginBottom: '0.5rem', fontSize: '1.1rem' }}>
                            ¿Estás seguro de que no se tomen las "Capturas de Seguridad"?
                        </p>
                        <p style={{ color: '#c2410c', fontSize: '1rem', fontWeight: '500' }}>
                            Presiona "Sí, Cancelar" nuevamente para registrar el acceso sin imágenes.
                        </p>
                    </div>
                )}

                {/* Mensaje informativo */}
                <div className={`captura-info-mensaje ${mostrarInfo ? 'expandido' : 'colapsado'}`}>
                    <div className="captura-info-header" onClick={() => setMostrarInfo(!mostrarInfo)}>
                        <div className="captura-info-titulo-wrapper">
                            <FaCamera className="captura-info-icono" />
                            <strong>Importante</strong>
                        </div>
                        <button type="button" className="btn-toggle-info">
                            {mostrarInfo ? 'Ocultar' : 'Ver detalle'}
                        </button>
                    </div>
                    {mostrarInfo && (
                        <p>
                            La captura de estas imágenes es por mayor
                            seguridad del visitante y de los residentes dentro del fraccionamiento.
                        </p>
                    )}
                </div>

                {/* Descripción de la imagen actual */}
                <p className="captura-descripcion">{tipoActual.descripcion}</p>

                {/* Área de cámara/preview */}
                <div className="captura-area">
                    {errorCamara && (
                        <div className="captura-error">
                            <p>{errorCamara}</p>
                            <button className="btn-reintentar" onClick={iniciarCamara}>
                                <FaRedo /> Reintentar
                            </button>
                        </div>
                    )}

                    {!mostrandoCamara && !imagenCapturada && !errorCamara && (
                        <div className="captura-placeholder">
                            <FaCamera className="captura-placeholder-icono" />
                            <p style={{ fontWeight: '600', marginBottom: '4px' }}>Presiona "Iniciar Cámara" para capturar la imagen</p>
                            <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                [Presiona "Sin Capturas" para no capturar imagenes de seguridad]
                            </p>
                        </div>
                    )}

                    {mostrandoCamara && (
                        <div className="captura-video-container">
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                className="captura-video"
                            />
                        </div>
                    )}

                    {imagenCapturada && (
                        <div className="captura-preview">
                            <img
                                src={imagenCapturada.url}
                                alt="Vista previa"
                                className="captura-preview-img"
                            />
                        </div>
                    )}

                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                </div>

                {/* Botones de acción */}
                <div className="captura-acciones">
                    {!mostrandoCamara && !imagenCapturada && (
                        <button
                            className="btn-captura btn-iniciar-camara"
                            onClick={iniciarCamara}
                            disabled={guardando}
                        >
                            <FaCamera /> Iniciar Cámara
                        </button>
                    )}

                    {mostrandoCamara && (
                        <button
                            className="btn-captura btn-tomar-foto"
                            onClick={capturarFoto}
                            disabled={guardando}
                        >
                            <FaCamera /> Tomar Foto
                        </button>
                    )}

                    {imagenCapturada && (
                        <>
                            <button
                                className="btn-captura btn-retomar"
                                onClick={retomar}
                                disabled={guardando}
                            >
                                <FaRedo /> Volver a Tomar
                            </button>
                            <button
                                className="btn-captura btn-guardar"
                                onClick={guardarImagenActual}
                                disabled={guardando}
                            >
                                <FaCheck /> {etapas.indexOf(etapaActual) === etapas.length - 1 ? 'Finalizar' : 'Guardar y Continuar'}
                            </button>
                        </>
                    )}

                    <button
                        className={`btn-captura btn-cancelar-captura ${confirmarCancelacion ? 'btn-cancelar-confirmado' : ''}`}
                        onClick={handleCancelar}
                        disabled={guardando}
                        style={confirmarCancelacion 
                            ? { backgroundColor: '#ef4444', color: 'white', fontWeight: 'bold' } 
                            : { backgroundColor: '#f59e0b', color: 'white' } 
                        }
                    >
                        {confirmarCancelacion ? 'Sí, Cancelar' : 'Sin Capturas'}
                    </button>

                    <button
                        className="btn-captura btn-salir-captura"
                        onClick={onExit || onCancel}
                        disabled={guardando}
                        style={{ backgroundColor: '#64748b', color: 'white' }}
                    >
                        Salir
                    </button>
                </div>

                {/* Indicadores de imágenes capturadas */}
                <div className="captura-indicadores">
                    {etapas.map(tipo => (
                        <div key={tipo} className={`captura-indicador ${imagenes[tipo] ? 'completado' : ''} ${etapaActual === tipo ? 'activo' : ''}`}>
                            {TIPOS_IMAGEN[tipo.toUpperCase()].icono}
                            <span>{tipo.toUpperCase()}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CapturaImagenesVisita;

